import { ClipboardEvent, CSSProperties, FocusEvent as ReactFocusEvent, KeyboardEvent, PointerEvent as ReactPointerEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core'
import ArrowDownward from '@material-ui/icons/ArrowDownward'
import ArrowUpward from '@material-ui/icons/ArrowUpward'
import DragIndicator from '@material-ui/icons/DragIndicator'
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined'
import Fullscreen from '@material-ui/icons/Fullscreen'
import FullscreenExit from '@material-ui/icons/FullscreenExit'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import Restore from '@material-ui/icons/Restore'
import ViewColumnOutlined from '@material-ui/icons/ViewColumnOutlined'
import clsx from 'clsx'
import { eachDayOfInterval, endOfMonth, format, startOfMonth } from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'

import palette from 'theme/palette'
import { StoreRemunerationValue } from '../BulkRemunerationValuesTable'
import { DailyGoalValues } from '../../types'
import { EXPANDED_CONTAINER_PADDING, useDailyGoalsTableStyles } from './styles'

interface CellPosition {
  column: number
  row: number
}

export type { DailyGoalValues } from '../../types'

interface DailyGoalsTableProps {
  disabled?: boolean
  month: string
  stores: StoreRemunerationValue[]
  values: DailyGoalValues
  onCompletionChange: (isComplete: boolean) => void
  onChange: (values: DailyGoalValues) => void
}

const getSelectionBounds = (start: CellPosition, end: CellPosition) => ({
  firstColumn: Math.min(start.column, end.column),
  firstRow: Math.min(start.row, end.row),
  lastColumn: Math.max(start.column, end.column),
  lastRow: Math.max(start.row, end.row)
})

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const ORDER_LIST_SCROLL_SPEED = 720
const NORMAL_DAY_ROW_HEIGHT = 20
const MIN_DAY_ROW_HEIGHT = 20
const MAX_DAY_ROW_HEIGHT = 40
const FIXED_TABLE_ROWS_HEIGHT = 80
const formatCurrency = (value: number) => currencyFormatter.format(value).replace(/\s/g, ' ')
const parseCurrency = (value: string) => {
  const parsed = Number(value.replace(/[^\d,-]/g, '').replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}
const sortStoreIds = (stores: StoreRemunerationValue[]) => [...stores]
  .sort((first, second) => first.storeName.localeCompare(second.storeName, 'pt-BR', { sensitivity: 'base' }))
  .map(store => store.storeId)

const writeTextToClipboard = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  const copied = document.execCommand('copy')
  textarea.remove()
  if (!copied) throw new Error('Unable to copy table')
}

export const formatTypedCurrency = (value: string): string => {
  if (value.includes('-')) return ''
  const digits = value.replace(/\D/g, '')
  return digits ? formatCurrency(Number(digits) / 100) : ''
}

export const formatPastedCurrency = (value: string): string => {
  const normalized = value.trim().replace(/R\$/gi, '').replace(/\s/g, '')
  if (!normalized || normalized.includes('-')) return ''
  const parsed = normalized.includes(',')
    ? Number(normalized.replace(/\./g, '').replace(',', '.'))
    : Number(normalized)
  return Number.isFinite(parsed) && parsed >= 0 ? formatCurrency(parsed) : ''
}

export const calculateExpandedDayRowHeight = (availableHeight: number, dayCount: number) => {
  if (availableHeight <= 0 || dayCount <= 0) return NORMAL_DAY_ROW_HEIGHT
  const availableDayRowsHeight = availableHeight - FIXED_TABLE_ROWS_HEIGHT
  return Math.max(MIN_DAY_ROW_HEIGHT, Math.min(MAX_DAY_ROW_HEIGHT, Math.floor(availableDayRowsHeight / dayCount)))
}

export const isExpandedTableVerticalScrollNeeded = (availableHeight: number, dayCount: number) => (
  dayCount > 0 && availableHeight < FIXED_TABLE_ROWS_HEIGHT + (MIN_DAY_ROW_HEIGHT * dayCount)
)

const DailyGoalsTable = ({ disabled = false, month, stores, values, onCompletionChange, onChange }: DailyGoalsTableProps) => {
  const classes = useDailyGoalsTableStyles()
  const containerRef = useRef<HTMLDivElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const tableViewportRef = useRef<HTMLDivElement>(null)
  const tableFrameRef = useRef<HTMLDivElement>(null)
  const storeOrderListRef = useRef<HTMLOListElement>(null)
  const dragPointRef = useRef<{ x: number; y: number } | null>(null)
  const orderDragYRef = useRef<number | null>(null)
  const editingCellRef = useRef<string | null>(null)
  const copyFeedbackTimeoutRef = useRef<number | null>(null)
  const suppressFocusSelectionRef = useRef(false)
  const currentValuesRef = useRef(values)
  const pendingValuesRef = useRef<DailyGoalValues | null>(null)
  const redoHistoryRef = useRef<DailyGoalValues[]>([])
  const undoHistoryRef = useRef<DailyGoalValues[]>([])
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')
  const [draftStoreOrder, setDraftStoreOrder] = useState<string[]>([])
  const [dragTargetStoreId, setDragTargetStoreId] = useState<string | null>(null)
  const [draggedStoreId, setDraggedStoreId] = useState<string | null>(null)
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [expandedDayRowHeight, setExpandedDayRowHeight] = useState(NORMAL_DAY_ROW_HEIGHT)
  const [isExpandedVerticalScrollNeeded, setIsExpandedVerticalScrollNeeded] = useState(false)
  const alphabeticalStoreIds = useMemo(() => sortStoreIds(stores), [stores])
  const [storeOrder, setStoreOrder] = useState<string[]>(() => sortStoreIds(stores))
  const orderedStores = useMemo(() => {
    const storesById = new Map(stores.map(store => [store.storeId, store]))
    return storeOrder.reduce<StoreRemunerationValue[]>((ordered, storeId) => {
      const store = storesById.get(storeId)
      if (store) ordered.push(store)
      return ordered
    }, [])
  }, [storeOrder, stores])
  const days = useMemo(() => {
    const monthDate = new Date(month)
    return eachDayOfInterval({ start: startOfMonth(monthDate), end: endOfMonth(monthDate) })
  }, [month])
  const dayKeys = useMemo(() => days.map(day => format(day, 'yyyy-MM-dd')), [days])
  const dayLabels = useMemo(() => days.map(day => {
    const dayLabel = format(day, 'dd/MM')
    const weekdayLabel = format(day, 'EEE', { locale: ptBR }).slice(0, 3).replace(/\./g, '')
    return { day: dayLabel, label: `${dayLabel} ${weekdayLabel}`, weekday: weekdayLabel }
  }), [days])
  const storeTotals = useMemo(() => orderedStores.map(store => dayKeys.reduce(
    (total, dayKey) => total + parseCurrency(values[dayKey]?.[store.storeId] || ''),
    0
  )), [dayKeys, orderedStores, values])
  const [selectionStart, setSelectionStart] = useState<CellPosition>({ row: 0, column: 0 })
  const [selectionEnd, setSelectionEnd] = useState<CellPosition>({ row: 0, column: 0 })
  const [isSelectionActive, setIsSelectionActive] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const getCellValue = (row: number, column: number) => values[dayKeys[row]]?.[orderedStores[column].storeId] || ''
  const filledCells = useMemo(() => dayKeys.reduce((total, dayKey) => (
    total + orderedStores.filter(store => Boolean(values[dayKey]?.[store.storeId])).length
  ), 0), [dayKeys, orderedStores, values])
  const totalCells = days.length * orderedStores.length
  const isComplete = totalCells > 0 && filledCells === totalCells
  const isDraftStoreOrderAlphabetical = draftStoreOrder.length === alphabeticalStoreIds.length &&
    draftStoreOrder.every((storeId, index) => storeId === alphabeticalStoreIds[index])

  useEffect(() => onCompletionChange(isComplete), [isComplete, onCompletionChange])
  useEffect(() => {
    if (!disabled) return
    setIsDragging(false)
    setIsSelectionActive(false)
  }, [disabled])
  useEffect(() => {
    setStoreOrder(currentOrder => {
      const availableStoreIds = new Set(alphabeticalStoreIds)
      const retainedStoreIds = currentOrder.filter(storeId => availableStoreIds.has(storeId))
      const addedStoreIds = alphabeticalStoreIds.filter(storeId => !currentOrder.includes(storeId))
      const nextOrder = [...retainedStoreIds, ...addedStoreIds]
      return nextOrder.join('|') === currentOrder.join('|') ? currentOrder : nextOrder
    })
  }, [alphabeticalStoreIds])
  useEffect(() => () => {
    if (copyFeedbackTimeoutRef.current !== null) window.clearTimeout(copyFeedbackTimeoutRef.current)
  }, [])
  useEffect(() => {
    currentValuesRef.current = values
    if (pendingValuesRef.current === values) {
      pendingValuesRef.current = null
      return
    }
    editingCellRef.current = null
    undoHistoryRef.current = []
    redoHistoryRef.current = []
  }, [values])
  useEffect(() => {
    const stopDragging = () => setIsDragging(false)
    const clearSelectionOutsideTable = (event: globalThis.PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        editingCellRef.current = null
        setIsSelectionActive(false)
      }
    }
    window.addEventListener('pointerup', stopDragging)
    window.addEventListener('pointercancel', stopDragging)
    document.addEventListener('pointerdown', clearSelectionOutsideTable)
    return () => {
      window.removeEventListener('pointerup', stopDragging)
      window.removeEventListener('pointercancel', stopDragging)
      document.removeEventListener('pointerdown', clearSelectionOutsideTable)
    }
  }, [])
  useLayoutEffect(() => {
    if (!isExpanded) return undefined

    const updateDayRowHeight = () => {
      const viewportHeight = tableViewportRef.current?.clientHeight || 0
      const frame = tableFrameRef.current
      const frameChromeHeight = frame ? frame.offsetHeight - frame.clientHeight : 0
      const availableHeight = Math.max(0, viewportHeight - frameChromeHeight)
      setExpandedDayRowHeight(calculateExpandedDayRowHeight(availableHeight, days.length))
      setIsExpandedVerticalScrollNeeded(isExpandedTableVerticalScrollNeeded(availableHeight, days.length))
    }
    updateDayRowHeight()
    window.addEventListener('resize', updateDayRowHeight)
    return () => window.removeEventListener('resize', updateDayRowHeight)
  }, [days.length, isExpanded, orderedStores.length])
  useEffect(() => {
    if (!isExpanded) return undefined
    const previousBodyOverflow = document.body.style.overflow
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && !isOrderDialogOpen) setIsExpanded(false)
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = previousBodyOverflow
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [isExpanded, isOrderDialogOpen])
  useEffect(() => {
    if (!draggedStoreId) {
      orderDragYRef.current = null
      return undefined
    }

    let animationFrameId: number
    let controlledScrollTop: number | null = null
    let previousFrameTime: number | null = null
    const trackDragPosition = (event: globalThis.DragEvent) => {
      if (event.clientX || event.clientY) orderDragYRef.current = event.clientY
    }
    const scrollOrderList = (frameTime: number) => {
      const list = storeOrderListRef.current
      const dragY = orderDragYRef.current
      if (list && dragY !== null) {
        const bounds = list.getBoundingClientRect()
        const threshold = Math.min(56, bounds.height / 3)
        const topScrollStart = bounds.top + threshold
        const bottomScrollStart = bounds.bottom - threshold
        const direction = dragY < topScrollStart ? -1 : dragY > bottomScrollStart ? 1 : 0
        if (direction) {
          if (controlledScrollTop === null) controlledScrollTop = list.scrollTop
          if (previousFrameTime !== null) {
            const elapsedSeconds = Math.min(frameTime - previousFrameTime, 50) / 1000
            controlledScrollTop += direction * ORDER_LIST_SCROLL_SPEED * elapsedSeconds
            list.scrollTop = controlledScrollTop
            controlledScrollTop = list.scrollTop
          }
          previousFrameTime = frameTime
        } else {
          controlledScrollTop = null
          previousFrameTime = null
        }
      } else {
        controlledScrollTop = null
        previousFrameTime = null
      }
      animationFrameId = window.requestAnimationFrame(scrollOrderList)
    }
    document.addEventListener('dragover', trackDragPosition)
    animationFrameId = window.requestAnimationFrame(scrollOrderList)
    return () => {
      document.removeEventListener('dragover', trackDragPosition)
      window.cancelAnimationFrame(animationFrameId)
      orderDragYRef.current = null
    }
  }, [draggedStoreId])
  useEffect(() => {
    if (!isDragging) {
      dragPointRef.current = null
      return undefined
    }

    let animationFrameId: number | null = null
    const updateSelectionAtPoint = (x: number, y: number) => {
      const target = document.elementFromPoint?.(x, y)
      const cell = target?.closest<HTMLElement>('[data-daily-goal-cell="true"]')
      if (!cell) return
      const row = Number(cell.dataset.dailyGoalGridRow)
      const column = Number(cell.dataset.dailyGoalGridColumn)
      if (!Number.isInteger(row) || !Number.isInteger(column)) return
      setSelectionEnd(current => current.row === row && current.column === column ? current : { row, column })
    }
    const getScrollState = (x: number, y: number) => {
      const frame = tableFrameRef.current
      if (!frame) return null
      const bounds = frame.getBoundingClientRect()
      const threshold = 48
      const horizontalDelta = x < bounds.left + threshold ? -14 : x > bounds.right - threshold ? 14 : 0
      const verticalDelta = isExpanded
        ? y < bounds.top + threshold ? -14 : y > bounds.bottom - threshold ? 14 : 0
        : y < threshold ? -14 : y > window.innerHeight - threshold ? 14 : 0
      return { bounds, frame, horizontalDelta, verticalDelta }
    }
    const updateSelectionForPoint = (x: number, y: number, bounds: DOMRect) => {
      const selectionX = Math.max(bounds.left + 66, Math.min(x, bounds.right - 2))
      const selectionY = Math.max(bounds.top + 42, Math.min(y, bounds.bottom - 2))
      updateSelectionAtPoint(selectionX, selectionY)
    }
    const scrollWhileDragging = () => {
      animationFrameId = null
      const point = dragPointRef.current
      if (point) {
        const scrollState = getScrollState(point.x, point.y)
        if (!scrollState) return
        const { bounds, frame, horizontalDelta, verticalDelta } = scrollState
        if (horizontalDelta) frame.scrollLeft += horizontalDelta
        if (isExpanded) {
          if (verticalDelta) frame.scrollTop += verticalDelta
        } else {
          if (verticalDelta) window.scrollBy(0, verticalDelta)
        }
        updateSelectionForPoint(point.x, point.y, bounds)
        if (horizontalDelta || verticalDelta) animationFrameId = window.requestAnimationFrame(scrollWhileDragging)
      }
    }
    const handlePointerMove = (event: globalThis.PointerEvent) => {
      if (!event.isPrimary) return
      dragPointRef.current = { x: event.clientX, y: event.clientY }
      const scrollState = getScrollState(event.clientX, event.clientY)
      if (!scrollState) return
      updateSelectionForPoint(event.clientX, event.clientY, scrollState.bounds)
      if ((scrollState.horizontalDelta || scrollState.verticalDelta) && animationFrameId === null) {
        animationFrameId = window.requestAnimationFrame(scrollWhileDragging)
      }
    }

    window.addEventListener('pointermove', handlePointerMove)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      if (animationFrameId !== null) window.cancelAnimationFrame(animationFrameId)
    }
  }, [isDragging, isExpanded])

  const selectionBounds = getSelectionBounds(selectionStart, selectionEnd)
  const isSelected = (row: number, column: number) => (
    isSelectionActive &&
    row >= selectionBounds.firstRow &&
    row <= selectionBounds.lastRow &&
    column >= selectionBounds.firstColumn &&
    column <= selectionBounds.lastColumn
  )

  const getSelectionOutline = (row: number, column: number) => {
    if (!isSelected(row, column)) return undefined
    const shadows = []
    if (row === selectionBounds.firstRow) shadows.push(`inset 0 1px ${palette.primary.main}`)
    if (row === selectionBounds.lastRow) shadows.push(`inset 0 -1px ${palette.primary.main}`)
    if (column === selectionBounds.firstColumn) shadows.push(`inset 1px 0 ${palette.primary.main}`)
    if (column === selectionBounds.lastColumn) shadows.push(`inset -1px 0 ${palette.primary.main}`)
    return shadows.join(', ')
  }

  const commitValues = (nextValues: DailyGoalValues, recordHistory = true) => {
    if (recordHistory) undoHistoryRef.current.push(currentValuesRef.current)
    redoHistoryRef.current = []
    currentValuesRef.current = nextValues
    pendingValuesRef.current = nextValues
    onChange(nextValues)
  }

  const updateCell = (row: number, column: number, value: string) => {
    const dayKey = dayKeys[row]
    const currentValues = currentValuesRef.current
    if ((currentValues[dayKey]?.[orderedStores[column].storeId] || '') === value) return
    const cellKey = `${dayKey}:${orderedStores[column].storeId}`
    const recordHistory = editingCellRef.current !== cellKey
    editingCellRef.current = cellKey
    commitValues({
      ...currentValues,
      [dayKey]: { ...currentValues[dayKey], [orderedStores[column].storeId]: value }
    }, recordHistory)
  }

  const restoreHistory = (direction: 'undo' | 'redo') => {
    editingCellRef.current = null
    const source = direction === 'undo' ? undoHistoryRef.current : redoHistoryRef.current
    const target = direction === 'undo' ? redoHistoryRef.current : undoHistoryRef.current
    const restoredValues = source.pop()
    if (!restoredValues) return
    target.push(currentValuesRef.current)
    currentValuesRef.current = restoredValues
    pendingValuesRef.current = restoredValues
    onChange(restoredValues)
  }

  const focusCell = (position: CellPosition) => {
    suppressFocusSelectionRef.current = true
    containerRef.current?.querySelector<HTMLInputElement>(
      `[data-daily-goal-row="${position.row}"][data-daily-goal-column="${position.column}"]`
    )?.focus()
  }

  const handleCellPointerDown = (event: ReactPointerEvent<HTMLInputElement>, position: CellPosition) => {
    if (!event.isPrimary || event.button !== 0) return
    event.preventDefault()
    editingCellRef.current = null
    setIsSelectionActive(true)
    if (!event.shiftKey) setSelectionStart(position)
    setSelectionEnd(position)
    setIsDragging(true)
    dragPointRef.current = { x: event.clientX, y: event.clientY }
    if (document.activeElement !== event.currentTarget) {
      suppressFocusSelectionRef.current = true
      event.currentTarget.focus()
    }
  }

  const handleCopy = (event: ClipboardEvent<HTMLDivElement>) => {
    const copiedRows = days
      .slice(selectionBounds.firstRow, selectionBounds.lastRow + 1)
      .map((_day, rowOffset) => orderedStores
        .slice(selectionBounds.firstColumn, selectionBounds.lastColumn + 1)
        .map((_store, columnOffset) => getCellValue(selectionBounds.firstRow + rowOffset, selectionBounds.firstColumn + columnOffset))
        .join('\t'))
    event.clipboardData.setData('text/plain', copiedRows.join('\n'))
    event.preventDefault()
  }

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const pastedRows = event.clipboardData
      .getData('text/plain')
      .replace(/\r/g, '')
      .replace(/\n$/, '')
      .split('\n')
      .map(row => row.split('\t').map(formatPastedCurrency))
    if (!pastedRows[0] || pastedRows[0].length === 0) return

    const shouldFillSelection = pastedRows.length === 1 && pastedRows[0].length === 1
    const firstRow = selectionBounds.firstRow
    const firstColumn = selectionBounds.firstColumn
    const lastRow = shouldFillSelection
      ? selectionBounds.lastRow
      : Math.min(days.length - 1, firstRow + pastedRows.length - 1)
    const lastColumn = shouldFillSelection
      ? selectionBounds.lastColumn
      : Math.min(orderedStores.length - 1, firstColumn + Math.max(...pastedRows.map(row => row.length)) - 1)

    const currentValues = currentValuesRef.current
    const nextValues = { ...currentValues }
    let hasChanges = false
    for (let row = firstRow; row <= lastRow; row++) {
      const dayKey = dayKeys[row]
      nextValues[dayKey] = { ...nextValues[dayKey] }
      for (let column = firstColumn; column <= lastColumn; column++) {
        const currentValue = currentValues[dayKey]?.[orderedStores[column].storeId] || ''
        const nextValue = shouldFillSelection
          ? pastedRows[0][0]
          : pastedRows[row - firstRow]?.[column - firstColumn] ?? currentValue
        nextValues[dayKey][orderedStores[column].storeId] = nextValue
        hasChanges = hasChanges || nextValue !== currentValue
      }
    }
    editingCellRef.current = null
    if (hasChanges) commitValues(nextValues)
    setSelectionStart({ row: firstRow, column: firstColumn })
    setSelectionEnd({ row: lastRow, column: lastColumn })
    event.preventDefault()
  }

  const handleCellKeyDown = (event: KeyboardEvent<HTMLInputElement>, position: CellPosition) => {
    const hasControlModifier = event.ctrlKey || event.metaKey
    const key = event.key.toLowerCase()
    if (hasControlModifier && key === 'a') {
      event.preventDefault()
      setIsSelectionActive(true)
      setSelectionStart({ row: 0, column: 0 })
      setSelectionEnd({ row: days.length - 1, column: orderedStores.length - 1 })
      return
    }
    if (hasControlModifier && key === 'z') {
      event.preventDefault()
      restoreHistory(event.shiftKey ? 'redo' : 'undo')
      return
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault()
      editingCellRef.current = null
      const currentValues = currentValuesRef.current
      const nextValues = { ...currentValues }
      let hasChanges = false
      for (let row = selectionBounds.firstRow; row <= selectionBounds.lastRow; row++) {
        const dayKey = dayKeys[row]
        nextValues[dayKey] = { ...nextValues[dayKey] }
        for (let column = selectionBounds.firstColumn; column <= selectionBounds.lastColumn; column++) {
          hasChanges = hasChanges || Boolean(nextValues[dayKey][orderedStores[column].storeId])
          nextValues[dayKey][orderedStores[column].storeId] = ''
        }
      }
      if (hasChanges) commitValues(nextValues)
      return
    }

    const arrowMovement = {
      ArrowDown: { row: 1, column: 0 },
      ArrowLeft: { row: 0, column: -1 },
      ArrowRight: { row: 0, column: 1 },
      ArrowUp: { row: -1, column: 0 }
    }[event.key]
    let nextPosition: CellPosition | null = null
    if (event.key === 'Enter') {
      event.preventDefault()
      const nextRow = position.row + (event.shiftKey ? -1 : 1)
      if (nextRow >= 0 && nextRow < days.length) nextPosition = { row: nextRow, column: position.column }
    } else if (event.key === 'Tab') {
      const currentIndex = position.row * orderedStores.length + position.column
      const nextIndex = currentIndex + (event.shiftKey ? -1 : 1)
      if (nextIndex >= 0 && nextIndex < totalCells) {
        nextPosition = {
          row: Math.floor(nextIndex / orderedStores.length),
          column: nextIndex % orderedStores.length
        }
      }
    } else if (arrowMovement) {
      nextPosition = {
        row: Math.max(0, Math.min(days.length - 1, position.row + arrowMovement.row)),
        column: Math.max(0, Math.min(orderedStores.length - 1, position.column + arrowMovement.column))
      }
    }
    if (!nextPosition) return
    if (nextPosition.row === position.row && nextPosition.column === position.column) return

    event.preventDefault()
    editingCellRef.current = null
    if (!event.shiftKey || event.key === 'Enter' || event.key === 'Tab') setSelectionStart(nextPosition)
    setSelectionEnd(nextPosition)
    focusCell(nextPosition)
  }

  const handleCellFocus = (event: ReactFocusEvent<HTMLInputElement>, position: CellPosition) => {
    event.currentTarget.select()
    setIsSelectionActive(true)
    if (suppressFocusSelectionRef.current) {
      suppressFocusSelectionRef.current = false
      return
    }
    setSelectionStart(position)
    setSelectionEnd(position)
  }

  const handleCopyTable = async () => {
    const header = ['Dia', ...orderedStores.map(store => store.storeName)].join('\t')
    const totals = ['Total', ...storeTotals.map(formatCurrency)].join('\t')
    const rows = dayLabels.map((dayLabel, row) => [
      dayLabel.label,
      ...orderedStores.map((_store, column) => getCellValue(row, column))
    ].join('\t'))
    try {
      await writeTextToClipboard([header, ...rows, totals].join('\n'))
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
    if (copyFeedbackTimeoutRef.current !== null) window.clearTimeout(copyFeedbackTimeoutRef.current)
    copyFeedbackTimeoutRef.current = window.setTimeout(() => setCopyStatus('idle'), 2500)
  }

  const openOrderDialog = () => {
    setDraftStoreOrder(storeOrder)
    setIsOrderDialogOpen(true)
  }

  const closeOrderDialog = () => {
    setDragTargetStoreId(null)
    setDraggedStoreId(null)
    setIsOrderDialogOpen(false)
  }

  const moveDraftStore = (storeId: string, offset: number) => {
    setDraftStoreOrder(currentOrder => {
      const currentIndex = currentOrder.indexOf(storeId)
      const nextIndex = Math.max(0, Math.min(currentOrder.length - 1, currentIndex + offset))
      if (currentIndex < 0 || currentIndex === nextIndex) return currentOrder
      const nextOrder = [...currentOrder]
      nextOrder.splice(currentIndex, 1)
      nextOrder.splice(nextIndex, 0, storeId)
      return nextOrder
    })
  }

  const dropDraftStore = (targetStoreId: string) => {
    if (!draggedStoreId || draggedStoreId === targetStoreId) return
    setDraftStoreOrder(currentOrder => {
      const draggedIndex = currentOrder.indexOf(draggedStoreId)
      const targetIndex = currentOrder.indexOf(targetStoreId)
      if (draggedIndex < 0 || targetIndex < 0) return currentOrder
      const nextOrder = [...currentOrder]
      nextOrder.splice(draggedIndex, 1)
      nextOrder.splice(targetIndex, 0, draggedStoreId)
      return nextOrder
    })
    setDragTargetStoreId(null)
    setDraggedStoreId(null)
  }

  const applyStoreOrder = () => {
    setStoreOrder(draftStoreOrder)
    setIsSelectionActive(false)
    editingCellRef.current = null
    closeOrderDialog()
  }

  const getStoreName = (storeId: string) => stores.find(store => store.storeId === storeId)?.storeName || ''

  const toggleExpanded = () => {
    if (isExpanded) {
      setIsExpanded(false)
      return
    }
    const toolbar = toolbarRef.current
    const frame = tableFrameRef.current
    const toolbarMarginBottom = toolbar ? parseFloat(window.getComputedStyle(toolbar).marginBottom) || 0 : 0
    const frameChromeHeight = frame ? frame.offsetHeight - frame.clientHeight : 0
    const availableHeight = window.innerHeight - (EXPANDED_CONTAINER_PADDING * 2) -
      (toolbar?.offsetHeight || 0) - toolbarMarginBottom - frameChromeHeight
    setExpandedDayRowHeight(calculateExpandedDayRowHeight(availableHeight, days.length))
    setIsExpandedVerticalScrollNeeded(isExpandedTableVerticalScrollNeeded(availableHeight, days.length))
    setIsExpanded(true)
  }

  return (
    <div
      ref={containerRef}
      className={clsx(classes.container, isExpanded && classes.expandedContainer)}
      style={{ '--daily-goal-expanded-row-height': `${expandedDayRowHeight}px` } as CSSProperties}
    >
      <div ref={toolbarRef} className={classes.toolbar}>
        <div className={classes.toolbarHints}>
          <span className={classes.toolbarHint}>
            <FileCopyOutlined />
            Cole dados do Excel ou selecione várias células para copiar e preencher em bloco
          </span>
          <span className={classes.toolbarHint}>
            <InfoOutlined />
            Preencha com R$ 0,00 para dias não trabalhados
          </span>
        </div>
        <span className={classes.toolbarActions}>
          <button className={classes.toolbarButton} disabled={disabled} type='button' onClick={handleCopyTable}>
            <FileCopyOutlined />
            {copyStatus === 'copied'
              ? 'Planilha copiada'
              : copyStatus === 'error' ? 'Tentar copiar novamente' : 'Copiar planilha'}
          </button>
          <button className={classes.toolbarButton} disabled={disabled} type='button' onClick={openOrderDialog}>
            <ViewColumnOutlined />
            Reordenar colunas
          </button>
          <button
            aria-label={isExpanded ? 'Restaurar tamanho da planilha' : 'Expandir planilha para ocupar a janela'}
            className={classes.toolbarButton}
            disabled={disabled}
            title={isExpanded ? 'Restaurar planilha' : 'Expandir planilha'}
            type='button'
            onClick={toggleExpanded}
          >
            {isExpanded ? <FullscreenExit /> : <Fullscreen />}
            {isExpanded ? 'Restaurar planilha' : 'Expandir planilha'}
          </button>
        </span>
      </div>
      <div ref={tableViewportRef} className={classes.tableViewport}>
        <div
          ref={tableFrameRef}
          className={clsx(
            classes.tableFrame,
            disabled && classes.disabledTableFrame,
            isExpanded && isExpandedVerticalScrollNeeded && classes.expandedScrollableTableFrame
          )}
          onCopy={handleCopy}
          onPaste={handlePaste}
        >
          <table aria-label='Metas diárias por loja' className={classes.table}>
            <thead>
              <tr>
                <th className={classes.dateHeader} scope='col' style={{ position: 'sticky', left: 0, zIndex: 5 }}>Dia</th>
                {orderedStores.map(store => <th key={store.storeId} scope='col'>{store.storeName}</th>)}
              </tr>
            </thead>
            <tbody>
              {days.map((day, row) => (
                <tr key={day.toISOString()}>
                  <th className={classes.dateCell} scope='row'>
                    {dayLabels[row].day} <span className={classes.weekdayLabel}>{dayLabels[row].weekday}</span>
                  </th>
                  {orderedStores.map((store, column) => (
                    <td
                      className={clsx(classes.cell, isSelected(row, column) && classes.selectedCell)}
                      data-daily-goal-cell='true'
                      data-daily-goal-grid-column={column}
                      data-daily-goal-grid-row={row}
                      key={store.storeId}
                      style={{ boxShadow: getSelectionOutline(row, column) }}
                    >
                      <input
                        aria-label={`Meta de ${store.storeName} em ${dayLabels[row].label}`}
                        className={classes.input}
                        data-daily-goal-column={column}
                        data-daily-goal-row={row}
                        disabled={disabled}
                        inputMode='numeric'
                        placeholder='R$ 0,00'
                        value={getCellValue(row, column)}
                        onBlur={() => { editingCellRef.current = null }}
                        onChange={event => updateCell(row, column, formatTypedCurrency(event.target.value))}
                        onFocus={event => handleCellFocus(event, { row, column })}
                        onKeyDown={event => handleCellKeyDown(event, { row, column })}
                        onPointerDown={event => handleCellPointerDown(event, { row, column })}
                      />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className={classes.totalRow} data-daily-goal-total-row='true'>
                <th scope='row'>Total</th>
                {orderedStores.map((store, column) => (
                  <td
                    aria-label={`Total das metas de ${store.storeName}`}
                    data-daily-goal-total-store={store.storeId}
                    key={store.storeId}
                  >
                    {formatCurrency(storeTotals[column])}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <Dialog
        disablePortal
        fullWidth
        aria-labelledby='daily-goals-order-title'
        classes={{ paper: classes.orderDialog }}
        maxWidth='xs'
        open={isOrderDialogOpen}
        onClose={closeOrderDialog}
      >
        <DialogTitle id='daily-goals-order-title' className={classes.orderDialogTitle}>
          Reordenar colunas
        </DialogTitle>
        <DialogContent className={classes.orderDialogContent}>
          <p className={classes.orderDialogDescription}>
            Arraste as lojas ou use as setas para definir a ordem das colunas.
          </p>
          <ol
            ref={storeOrderListRef}
            className={classes.storeOrderList}
            onDragOver={event => event.preventDefault()}
          >
            {draftStoreOrder.map((storeId, index) => (
              <li
                aria-label={`${index + 1}. ${getStoreName(storeId)}`}
                className={clsx(
                  classes.storeOrderItem,
                  draggedStoreId === storeId && classes.draggedStoreOrderItem,
                  dragTargetStoreId === storeId && draggedStoreId !== storeId && classes.dragTargetStoreOrderItem
                )}
                draggable
                key={storeId}
                onDragEnd={() => {
                  setDragTargetStoreId(null)
                  setDraggedStoreId(null)
                }}
                onDragEnter={() => setDragTargetStoreId(storeId)}
                onDragOver={event => event.preventDefault()}
                onDragStart={event => {
                  event.dataTransfer?.setData('text/plain', storeId)
                  setDraggedStoreId(storeId)
                }}
                onDrop={() => dropDraftStore(storeId)}
              >
                <span className={classes.dragHandle} aria-hidden='true'><DragIndicator /></span>
                <span className={classes.storeOrderPosition}>{index + 1}</span>
                <span className={classes.storeOrderName}>{getStoreName(storeId)}</span>
                <span className={classes.storeOrderControls}>
                  <button
                    aria-label={`Mover ${getStoreName(storeId)} para cima`}
                    className={classes.storeOrderButton}
                    disabled={index === 0}
                    type='button'
                    onClick={() => moveDraftStore(storeId, -1)}
                  >
                    <ArrowUpward />
                  </button>
                  <button
                    aria-label={`Mover ${getStoreName(storeId)} para baixo`}
                    className={classes.storeOrderButton}
                    disabled={index === draftStoreOrder.length - 1}
                    type='button'
                    onClick={() => moveDraftStore(storeId, 1)}
                  >
                    <ArrowDownward />
                  </button>
                </span>
              </li>
            ))}
          </ol>
        </DialogContent>
        <DialogActions className={classes.orderDialogActions}>
          <button
            className={classes.restoreOrderButton}
            disabled={isDraftStoreOrderAlphabetical}
            type='button'
            onClick={() => setDraftStoreOrder(alphabeticalStoreIds)}
          >
            <Restore />
            Ordem alfabética
          </button>
          <button
            className={classes.cancelOrderButton}
            type='button'
            onClick={closeOrderDialog}
          >
            Cancelar
          </button>
          <button className={classes.applyOrderButton} type='button' onClick={applyStoreOrder}>
            Aplicar ordem
          </button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default DailyGoalsTable
